// src/components/AgentOps.jsx
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Shield, Network, Wrench, Server, AlertTriangle, FileCode, Upload, Play, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import API from '../../services/api';
import ForceGraph2D from 'react-force-graph-2d';
import { parseScanReport } from "../../utils/parseScanReport";
import { useAuth } from '../../context/AuthContext';
import Logo from '../../assets/reportlogo.png';
import KPMGlogo from '../../assets/kpmglogo.png';

const AgentOps = () => {
  const [activeTab, setActiveTab] = useState('scan');
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [files, setFiles] = useState([]);
  const [scanResult, setScanResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [scanType, setScanType] = useState('Static Analysis');
  const [alertState, setAlertState] = useState(null);
  const [expandedResult, setExpandedResult] = useState(null);
  const [expandedIO, setExpandedIO] = useState({});
  const [selectedDynamicScan, setSelectedDynamicScan] = useState(null);
  const [selectedDynamicScanData, setSelectedDynamicScanData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [dynamicScanList, setDynamicScanList] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const toggleIO = (key) => setExpandedIO(prev => ({ ...prev, [key]: !prev[key] }));
  const selectedApp = sessionStorage.getItem("selectedApp")

  const fetchDynamicScans = async () => {
    setLoadingScans(true);
    try {
      const res = await API.get('/agentic/scan-files/');
      const mapped = (res.data.files || []).map(f => ({
        id: `${encodeURIComponent(f.asset_name)}/${encodeURIComponent(f.name)}`,
        name: f.name,
        asset_name: f.asset_name,
        date: f.created_at ? new Date(f.created_at).toLocaleString('en-GB', { timeZone: 'UTC', hour12: false }).replace(',', '') + ' UTC' : '-',
      }));
      setDynamicScanList(mapped);
    } catch {
      setDynamicScanList([]);
    } finally {
      setLoadingScans(false);
    }
  };

  useEffect(() => {
    fetchDynamicScans();
  }, []);

  const viewDynamicScan = async (scan) => {
    setSelectedDynamicScan(scan);
    setSelectedDynamicScanData(null);
    setExpandedResult(null);
    setLoadingDetail(true);
    try {
      const res = await API.get('/agentic/scan-file-content/', {
        params: { asset_name: scan.asset_name, filename: scan.name },
      });
      setSelectedDynamicScanData(res.data);
    } catch {
      setSelectedDynamicScanData(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const { tenant } = useAuth();

  const graphContainerRef = useRef(null);
  const alertTimerRef = useRef(null);

  // Extract parsed data from scanResult with safe defaults
  const agents = scanResult?.result?.agents || [];
  const tools = scanResult?.result?.tools || [];
  const mcpServers = scanResult?.result?.mcpServers || [];
  const vulnerabilities = scanResult?.result?.vulnerabilities || [];
  const graphData = scanResult?.result?.graphData || null;
  const scanDetails = scanResult?.result?.scanDetails || {};
  const findings = scanResult?.result?.findings || {};

  const activeVulnerabilities = vulnerabilities.filter(v => !v.mitigated);

  const tintSvgDataUri = (dataUri, color) => {
    if (!dataUri || !dataUri.startsWith('data:image/svg+xml;base64,')) return dataUri;
    try {
      const base64 = dataUri.split(',')[1];
      const svg = atob(base64);
      const tinted = svg
        .replace(/fill="(#000000|#000|black)"/gi, `fill="${color}"`)
        .replace(/stroke="(#000000|#000|black)"/gi, `stroke="${color}"`);
      return `data:image/svg+xml;base64,${btoa(tinted)}`;
    } catch (e) {
      return dataUri;
    }
  };

  const processedGraphData = useMemo(() => {
    if (!graphData || !graphData.nodes) return graphData;
    return {
      ...graphData,
      nodes: graphData.nodes.map((node) => ({
        ...node,
        image: node.image ? tintSvgDataUri(node.image, '#e2e8f0') : node.image
      }))
    };
  }, [graphData]);

  // const fgRef = useRef();
  const normalizeFW = fw =>
    fw?.trim()
      .replace(/AI/i, '')      // remove "AI" if present
      .replace(/\s+/g, ' ')    // collapse spaces
      .replace(/[:\s]+$/g, '') // remove trailing colons/spaces
      .trim();


  //console.log("graphdata", graphData);

  useEffect(() => {
    fetchFrameworks();
  }, []);

  useEffect(() => {
    return () => {
      if (alertTimerRef.current) {
        clearTimeout(alertTimerRef.current);
      }
    };
  }, []);

  const showAlert = (type, message, timeoutMs = 4000) => {
    setAlertState({ type, message });
    if (alertTimerRef.current) {
      clearTimeout(alertTimerRef.current);
    }
    alertTimerRef.current = setTimeout(() => {
      setAlertState(null);
      alertTimerRef.current = null;
    }, timeoutMs);
  };

  const getAlertStyles = (type) => {
    switch (type) {
      case 'success':
        return {
          container: 'border-emerald-500/40 bg-emerald-500/10',
          icon: 'text-emerald-400',
          title: 'Success',
        };
      case 'warning':
        return {
          container: 'border-yellow-500/40 bg-yellow-500/10',
          icon: 'text-yellow-400',
          title: 'Warning',
        };
      case 'error':
        return {
          container: 'border-red-500/40 bg-red-500/10',
          icon: 'text-red-400',
          title: 'Error',
        };
      default:
        return {
          container: 'border-blue-500/40 bg-blue-500/10',
          icon: 'text-blue-400',
          title: 'Notice',
        };
    }
  };

  const fetchFrameworks = async () => {
    try {
      const res = await API.get('/agentic/frameworks/');
      const data = Array.isArray(res.data)
        ? res.data
        : res.data.results || res.data.frameworks || [];
      setFrameworks(data);
      if (data.length) setSelectedFramework(data[0].slug);
    } catch (err) {
      console.error('Failed to load frameworks:', err);
      setFrameworks([]);
    }
  };

  const onFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const applyReportTheme = (htmlString, meta = {}) => {
    if (!htmlString) return htmlString;

    const {
      logoBase64 = '',
      kpmgBase64 = '',
      toolName = 'LLMINATE',
      reportTitle = 'Agentic Radar Report'
    } = meta;

    const headerHtml = `
      <div class="llminate-header">
        <div class="llminate-header-left">
          ${logoBase64 ? `<img src="${logoBase64}" class="llminate-logo" alt="LLMINATE" />` : ''}
          <div class="llminate-title">
            <div class="llminate-brand">${toolName}</div>
            <div class="llminate-subtitle">${reportTitle}</div>
          </div>
        </div>
        <div class="llminate-header-right">
          ${kpmgBase64 ? `<img src="${kpmgBase64}" class="llminate-kpmg" alt="KPMG" />` : ''}
        </div>
      </div>
    `;

    let themedHtml = htmlString
      .replace(/background(?:-color)?:\s*#fff\b/gi, 'background-color: #0b1220')
      .replace(/background(?:-color)?:\s*#ffffff\b/gi, 'background-color: #0b1220')
      .replace(/background(?:-color)?:\s*white\b/gi, 'background-color: #0b1220')
      .replace(/color:\s*#000\b/gi, 'color: #e2e8f0')
      .replace(/color:\s*#111\b/gi, 'color: #e2e8f0')
      .replace(/color:\s*#222\b/gi, 'color: #e2e8f0')
      .replace(/color:\s*black\b/gi, 'color: #e2e8f0');

    const themeStyles = `
      :root {
        color-scheme: dark;
      }
      html, body {
        background: #000 !important;
        color: #e2e8f0 !important;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        line-height: 1.6 !important;
      }
      body { padding: 32px !important; }
      a { color: #60a5fa !important; }
      h1, h2, h3, h4, h5, h6 {
        color: #fff !important;
        letter-spacing: 0.2px;
      }
      hr { border-color: #1e293b !important; }
      table {
        width: 100% !important;
        border-collapse: collapse !important;
        background: #0b1220 !important;
        border: 1px solid #1e293b !important;
        border-radius: 10px;
        overflow: hidden;
      }
      th, td {
        border-bottom: 1px solid #1e293b !important;
        padding: 10px 12px !important;
        vertical-align: top;
        color: #e2e8f0 !important;
      }
      th {
        text-transform: uppercase;
        font-size: 12px !important;
        letter-spacing: 0.6px !important;
        color: #94a3b8 !important;
        background: #0f172a !important;
      }
      tr:nth-child(even) td {
        background: rgba(15, 23, 42, 0.45) !important;
      }
      code, pre {
        background: rgba(15, 23, 42, 0.6) !important;
        color: #e2e8f0 !important;
        border: 1px solid #1e293b !important;
        border-radius: 6px;
        padding: 8px 10px !important;
        overflow-x: auto;
      }
      .container, .report, .content, .page, .section, .card {
        max-width: 1200px;
        margin: 0 auto;
      }
      .main {
        background: #0b1220 !important;
        color: #e2e8f0 !important;
        border: 1px solid #1e293b !important;
        border-radius: 12px;
        padding: 24px;
      }
      .llminate-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding-bottom: 16px;
        margin-bottom: 16px;
        border-bottom: 1px solid #1e293b;
      }
      .llminate-header-left {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .llminate-logo {
        width: 52px;
        height: 52px;
        object-fit: contain;
      }
      .llminate-title {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }
      .llminate-brand {
        font-size: 22px;
        font-weight: 800;
        color: #60a5fa;
        letter-spacing: 0.5px;
      }
      .llminate-subtitle {
        font-size: 16px;
        color: #cbd5e1;
      }
      .llminate-kpmg {
        height: 26px;
        object-fit: contain;
        filter: brightness(1.2);
      }
      .llminate-meta {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
        padding: 12px 16px;
        margin-bottom: 20px;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid #1e293b;
        border-radius: 10px;
      }
      .llminate-meta-item span {
        display: block;
        font-size: 11px;
        letter-spacing: 0.6px;
        text-transform: uppercase;
        color: #94a3b8;
        margin-bottom: 4px;
      }
      .llminate-meta-item strong {
        font-size: 13px;
        color: #e2e8f0;
        font-weight: 600;
        word-break: break-word;
      }
      .graph-container {
        background: #0b1220 !important;
        border: 1px solid #1e293b !important;
        color: #e2e8f0 !important;
        overflow: hidden !important;
      }
      .graph-container * {
        color: #e2e8f0 !important;
      }
      #graph {
        background: transparent !important;
        overflow: hidden !important;
      }
      .legend-title, .legend-text, .section-title {
        color: #e2e8f0 !important;
      }
      [style*="background: #fff"], [style*="background:#fff"], [style*="background-color: #fff"], [style*="background-color:#fff"] {
        background: #0b1220 !important;
        color: #e2e8f0 !important;
      }
    `;

    const themeScript = `
      <script>
        (function() {
          function apply() {
            document.documentElement.style.backgroundColor = '#000';
            document.body.style.backgroundColor = '#000';
            document.body.style.color = '#e2e8f0';
            var main = document.querySelector('.main');
            if (main) {
              main.style.backgroundColor = '#0b1220';
              main.style.color = '#e2e8f0';
              main.style.border = '1px solid #1e293b';
              main.style.borderRadius = '12px';
            }
            document.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach(function(el) {
              el.style.color = '#ffffff';
            });
            document.querySelectorAll('table, th, td').forEach(function(el) {
              el.style.color = '#e2e8f0';
              el.style.borderColor = '#1e293b';
            });
            document.querySelectorAll('.card, .section, .panel').forEach(function(el) {
              el.style.backgroundColor = 'rgba(15, 23, 42, 0.6)';
              el.style.borderColor = '#1e293b';
              el.style.color = '#e2e8f0';
            });

            try {
              if (typeof graph !== 'undefined' && graph) {
                graph
                  .linkColor(() => 'rgba(226, 232, 240, 0.45)')
                  .linkDirectionalArrowColor(() => 'rgba(226, 232, 240, 0.65)')
                  .nodeCanvasObject((n, ctx, scale) => {
                    if (n.image) {
                      const size = 8;
                      ctx.drawImage(n.image, n.x - size / 2, n.y - size / 2, size, size);
                    }
                    ctx.font = (10 / scale) + 'px Inter';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillStyle = '#e2e8f0';
                    ctx.fillText(n.label, n.x, n.y + 6);
                  })
                  .nodeCanvasObjectMode(() => "replace");
                if (typeof graph.refresh === 'function') {
                  graph.refresh();
                }
              }
            } catch (e) {
              // If graph isn't in scope, skip customization
            }
          }
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', apply);
          } else {
            apply();
          }
        })();
      </script>
    `;

    if (!themedHtml.includes('llminate-header')) {
      themedHtml = themedHtml.replace(/<body[^>]*>/i, (match) => `${match}${headerHtml}`);
    }

    if (themedHtml.includes('</head>')) {
      themedHtml = themedHtml.replace(
        '</head>',
        `<style>${themeStyles}</style></head>`
      );
    } else {
      themedHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />
            <title>Agentic Radar Report</title>
            <style>${themeStyles}</style>
          </head>
          <body>
            ${themedHtml}
            <style>${themeStyles}</style>
            ${themeScript}
          </body>
        </html>
      `;
      return themedHtml;
    }

    if (themedHtml.includes('</body>')) {
      themedHtml = themedHtml.replace('</body>', `<style>${themeStyles}</style>${themeScript}</body>`);
    }

    return themedHtml;
  };
  
  const tenantName = tenant || sessionStorage.getItem('tenantName') || localStorage.getItem('tenantName') || 'N/A';

  const startScan = async (runTests = false) => {
    if (!selectedFramework) {
      showAlert('warning', 'Select a framework first.');
      return;
    }
    if (!files.length) {
      showAlert('warning', 'Please upload at least one file before starting a scan.');
      return;
    }

    const fw = frameworks.find(f => f.slug === selectedFramework);
    if (!fw) {
      showAlert('error', 'Invalid framework selected.');
      return;
    }

    if (runTests && !fw.supports_agentic_test) {
      showAlert('warning', 'Selected framework does not support agentic tests.');
      return;
    }

    const fd = new FormData();
    fd.append("framework", selectedFramework);
    fd.append("scan_type", scanType);
    fd.append("run_tests", runTests ? "true" : "false");
    fd.append("selected_app", selectedApp);
    files.forEach(f => fd.append("files", f));

    setRunning(true);
    setScanResult(null);

    try {
      const res = await API.post("agentic/scan/", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const reportHtml = res.data?.result?.report_html;
      let extracted = {};

      if (reportHtml) {
        //console.log('📄 Parsing HTML report...');
        extracted = parseScanReport(reportHtml);
        //console.log('✅ Parsed data:', extracted);
      } else {
        console.warn('⚠️ No report_html found in response');
      }

      // Merge the parsed data with the original response
      setScanResult({
        ...res.data,
        result: {
          ...res.data.result,
          ...extracted
        }
      });

      console.log('📊 Final scan result:', {
        ...res.data,
        result: {
          ...res.data.result,
          ...extracted
        }
      });

      // Auto-download HTML report
      if (reportHtml) {
        const loadImageAsBase64 = async (src) => {
          try {
            const response = await fetch(src);
            const blob = await response.blob();
            return await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (err) {
            return '';
          }
        };

        const [logoBase64, kpmgBase64] = await Promise.all([
          loadImageAsBase64(Logo),
          loadImageAsBase64(KPMGlogo)
        ]);

        const themedReportHtml = applyReportTheme(reportHtml, {
          logoBase64,
          kpmgBase64,
          tenantName: tenant || sessionStorage.getItem('tenantName') || localStorage.getItem('tenantName') || 'N/A',
          appName: selectedApp || 'N/A',
          toolName: 'LLMINATE',
          reportTitle: 'Agentic Radar Report'
        });
        const blob = new Blob([themedReportHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `scan_report_${selectedFramework}_${Date.now()}.html`;
        a.click();
        URL.revokeObjectURL(url);
      }

      setActiveTab("vulnerabilities");
    } catch (e) {
      showAlert('error', "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const runTests = async (testSuite = 'Default Security Tests') => {
    if (!selectedFramework) {
      showAlert('warning', 'Select a framework first.');
      return;
    }
    const fw = frameworks.find(f => f.slug === selectedFramework);
    if (!fw) {
      showAlert('error', 'Invalid framework selected.');
      return;
    }
    if (!fw.supports_agentic_test) {
      showAlert('warning', 'Selected framework does not support agentic tests.');
      return;
    }

    setRunning(true);
    try {
      const res = await API.post('agentic/test/', {
        framework: selectedFramework,
        test_suite: testSuite,
        api_config: 'OpenAI API Key',
      });
      showAlert('success', 'Tests completed - see console for details.');
      console.log('Test results:', res.data);
    } catch (e) {
      console.error('Tests failed:', e);
      showAlert('error', 'Tests failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  const selectedFW = frameworks.find(f => f.slug === selectedFramework);
  const supportsTests = selectedFW?.supports_agentic_test;

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[severity?.toLowerCase()] || 'bg-gray-500';
  };

  const getSeverityTextColor = (severity) => {
    const colors = {
      critical: 'text-red-400',
      high: 'text-orange-400',
      medium: 'text-yellow-400',
      low: 'text-green-400'
    };
    return colors[severity?.toLowerCase()] || 'text-gray-400';
  };

  return (
    <div className="min-h-screen max-w-[85vw] bg-black text-white p-6">
      {alertState && (() => {
        const styles = getAlertStyles(alertState.type);
        return (
          <div className={`mb-6 border ${styles.container} rounded-xl px-4 py-3 flex items-start gap-3 shadow-lg`}>
            <AlertTriangle className={`${styles.icon} mt-0.5`} size={18} />
            <div className="flex-1">
              <div className={`text-md font-semibold ${styles.icon}`}>{styles.title}</div>
              <div className="text-md text-gray-200">{alertState.message}</div>
            </div>
            <button
              type="button"
              onClick={() => setAlertState(null)}
              className="text-gray-400 hover:text-red-400 transition-colors text-md"
            >
              X
            </button>
          </div>
        );
      })()}
      <div className="mb-6 m-3">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-4xl font-bold">Agentic Radar</h1>
        </div>
        <p className="text-gray-400 text-lg">AI Workflow Security Scanner & Vulnerability Mapper</p>
      </div>

      {/* Top-level Static / Dynamic tabs — full width, equal split */}
      <div className="flex w-full mb-6 rounded-xl overflow-hidden border border-gray-800 bg-gradient-to-b from-gray-900/80 to-gray-950/80 shadow-inner">
        {[
          { id: 'static', label: 'Static Scans' },
          { id: 'dynamic', label: 'Dynamic Scans' },
        ].map(({ id, label }, i) => {
          const isActive = (id === 'static' && activeTab !== 'dynamic') || (id === 'dynamic' && activeTab === 'dynamic')
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id === 'static' ? 'scan' : 'dynamic')}
              className={`flex-1 py-3.5 text-base font-semibold transition-all relative ${
                i === 0 ? 'border-r border-gray-800' : ''
              } ${
                isActive
                  ? 'text-blue-400 bg-blue-500/10'
                  : 'text-gray-500 hover:text-gray-200 hover:bg-white/5'
              }`}
            >
              {isActive && (
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-10 h-0.5 rounded-full bg-blue-500" />
              )}
              {label}
            </button>
          )
        })}
      </div>

      {/* Dynamic tab — scan list */}
      {activeTab === 'dynamic' && !selectedDynamicScan && (
        <div className="bg-transparent border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-900/40">
            <div>
              <p className="text-white font-semibold">Dynamic Scans</p>
              <p className="text-gray-400 text-sm mt-0.5">AgentOps security test results per scan run.</p>
            </div>
          </div>
          {loadingScans ? (
            <div className="py-10 text-center text-gray-500 text-sm">Loading scans…</div>
          ) : dynamicScanList.length === 0 ? (
            <div className="py-10 text-center text-gray-500 text-sm">No scans yet</div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-white min-w-[600px]">
              <thead>
                <tr className="bg-gray-900/60">
                  <th className="px-5 py-3 border-b border-gray-800 text-gray-400 text-sm font-medium text-left">Sl. No</th>
                  <th className="px-5 py-3 border-b border-gray-800 text-gray-400 text-sm font-medium text-left">Scan Name</th>
                  <th className="px-5 py-3 border-b border-gray-800 text-gray-400 text-sm font-medium text-left">Date</th>
                  <th className="px-5 py-3 border-b border-gray-800 text-gray-400 text-sm font-medium text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {dynamicScanList.map((scan, index) => (
                  <tr key={scan.id} className="hover:bg-gray-800/30 transition-colors border-t border-gray-800/60">
                    <td className="px-5 py-3.5 text-gray-500 text-sm">{index + 1}</td>
                    <td className="px-5 py-3.5 text-gray-200 text-sm">{scan.name}</td>
                    <td className="px-5 py-3.5 text-gray-400 text-sm whitespace-nowrap">{scan.date}</td>
                    <td className="px-5 py-3.5 text-center">
                      <button
                        onClick={() => viewDynamicScan(scan)}
                        className="bg-blue-500/10 hover:bg-blue-600/20 px-3 py-1 rounded-full text-xs font-medium text-blue-400 border border-blue-500/20 transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {/* Dynamic tab — scan detail view */}
      {activeTab === 'dynamic' && selectedDynamicScan && (() => {
        if (loadingDetail) return (
          <div className="py-20 text-center text-gray-500 text-sm">Loading scan details…</div>
        )
        if (!selectedDynamicScanData) return (
          <div className="py-20 text-center text-gray-500 text-sm">Failed to load scan data.</div>
        )
        const results = selectedDynamicScanData.results || []
        const total = results.length
        const passed = results.filter(r => r.test_passed === 'PASSED').length
        const failed = total - passed
        const passRate = total > 0 ? Math.round((passed / total) * 100) : 0
        const testTypes = [...new Set(results.map(r => r.test_name))]
        const agentNames = [...new Set(results.map(r => r.agent_name))]
        const testColors = {
          'Fake News': 'text-orange-400 bg-orange-500/10 border-orange-500/20',
          'Prompt Injection': 'text-purple-400 bg-purple-500/10 border-purple-500/20',
          'Harmful Content': 'text-red-400 bg-red-500/10 border-red-500/20',
          'PII Leakage': 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
        }

        return (
          <div className="space-y-6">
            {/* Back + header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setSelectedDynamicScan(null); setSelectedDynamicScanData(null); }}
                  className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  <ChevronUp size={14} className="rotate-[-90deg]" />
                  Back
                </button>
                <span className="text-gray-700">/</span>
                <span className="text-white text-sm font-medium">{selectedDynamicScan.name}</span>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Timestamp</p>
                <p className="text-gray-300 text-sm font-medium mt-0.5">{selectedDynamicScanData.timestamp ? new Date(selectedDynamicScanData.timestamp).toLocaleString() : selectedDynamicScan.date}</p>
              </div>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Total Tests', value: total, color: 'text-white' },
                { label: 'Passed', value: passed, color: 'text-emerald-400' },
                { label: 'Failed', value: failed, color: 'text-red-400' },
                { label: 'Pass Rate', value: `${passRate}%`, color: passRate >= 80 ? 'text-emerald-400' : passRate >= 60 ? 'text-yellow-400' : 'text-red-400' },
              ].map(stat => (
                <div key={stat.label} className="bg-transparent border border-gray-800 rounded-xl p-5">
                  <div className={`text-3xl font-bold ${stat.color}`}>{stat.value}</div>
                  <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Test type breakdown */}
            <div className="bg-transparent border border-gray-800 rounded-xl p-5">
              <p className="text-white font-semibold mb-4">Attack Category Breakdown</p>
              <div className="grid grid-cols-4 gap-3">
                {testTypes.map(type => {
                  const typeResults = results.filter(r => r.test_name === type)
                  const typePassed = typeResults.filter(r => r.test_passed === 'PASSED').length
                  const typeFailed = typeResults.length - typePassed
                  return (
                    <div key={type} className={`border rounded-lg p-4 ${testColors[type] || 'text-gray-400 bg-gray-800/30 border-gray-700'}`}>
                      <p className="font-semibold text-sm mb-3">{type}</p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1 text-emerald-400"><CheckCircle size={12} /> {typePassed}</span>
                        <span className="flex items-center gap-1 text-red-400"><AlertTriangle size={12} /> {typeFailed}</span>
                      </div>
                      <div className="mt-3 h-1 rounded-full bg-gray-700/50">
                        <div className="h-1 rounded-full bg-emerald-500" style={{ width: `${Math.round((typePassed / typeResults.length) * 100)}%` }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Per-agent results */}
            <div className="space-y-4">
              {agentNames.map(agent => {
                const agentResults = results.filter(r => r.agent_name === agent)
                const agentPassed = agentResults.filter(r => r.test_passed === 'PASSED').length
                const agentFailed = agentResults.length - agentPassed
                return (
                  <div key={agent} className="bg-transparent border border-gray-800 rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between bg-gray-900/40">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                          <Shield size={15} className="text-blue-400" />
                        </div>
                        <span className="text-white font-semibold text-base capitalize">{agent.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1.5 text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-full font-medium">
                          <CheckCircle size={12} /> {agentPassed} passed
                        </span>
                        {agentFailed > 0 && (
                          <span className="flex items-center gap-1.5 text-red-400 bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded-full font-medium">
                            <AlertTriangle size={12} /> {agentFailed} failed
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="divide-y divide-gray-800/60">
                      {agentResults.map((result, idx) => {
                        const key = `${agent}-${result.test_name}`
                        const isExpanded = expandedResult === key
                        const isPassed = result.test_passed === 'PASSED'
                        const ioKey = `${key}-io`
                        const isIOExpanded = expandedIO[ioKey]
                        return (
                          <div key={idx}>
                            <button
                              onClick={() => setExpandedResult(isExpanded ? null : key)}
                              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-800/30 transition-colors text-left"
                            >
                              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isPassed ? 'bg-emerald-400' : 'bg-red-400'}`} />
                              <span className="text-gray-200 text-base flex-1 font-medium">{result.test_name}</span>
                              <span className={`text-xs font-bold px-3 py-1 rounded-full border tracking-wide ${isPassed ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-red-400 bg-red-500/10 border-red-500/20'}`}>
                                {result.test_passed}
                              </span>
                              {isExpanded ? <ChevronUp size={16} className="text-gray-500 shrink-0" /> : <ChevronDown size={16} className="text-gray-500 shrink-0" />}
                            </button>
                            {isExpanded && (
                              <div className="px-6 pb-6 pt-3 bg-gray-900/30 space-y-5 border-t border-gray-800/40">
                                {/* Explanation */}
                                <div>
                                  <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 font-semibold">Explanation</p>
                                  <p className="text-gray-200 text-sm leading-7">{result.explanation}</p>
                                </div>

                                {/* Input / Output */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 font-semibold">Input (Attack Prompt)</p>
                                    <div className={`bg-[#0d0d0f] border border-gray-800 rounded-xl p-4 relative overflow-hidden transition-all duration-300 ${isIOExpanded ? '' : 'max-h-36'}`}>
                                      <p className="text-gray-300 text-sm font-mono leading-7 whitespace-pre-wrap">{result.input}</p>
                                      {!isIOExpanded && (
                                        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0d0d0f] to-transparent rounded-b-xl" />
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500 uppercase tracking-widest mb-2 font-semibold">Agent Output</p>
                                    <div className={`bg-[#0d0d0f] border border-gray-800 rounded-xl p-4 relative overflow-hidden transition-all duration-300 ${isIOExpanded ? '' : 'max-h-36'}`}>
                                      <p className="text-gray-300 text-sm font-mono leading-7 whitespace-pre-wrap">{result.output}</p>
                                      {!isIOExpanded && (
                                        <div className="absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-[#0d0d0f] to-transparent rounded-b-xl" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => toggleIO(ioKey)}
                                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium flex items-center gap-1"
                                >
                                  {isIOExpanded ? <><ChevronUp size={13} /> Show less</> : <><ChevronDown size={13} /> Show full message</>}
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })()}

      {activeTab !== 'dynamic' && (<>

      {/* Static sub-tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800">
        {['scan', 'workflow', 'vulnerabilities'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-6 py-3 font-semibold capitalize transition-all border-b-2 -mb-px ${
              activeTab === tab
                ? 'text-blue-500 border-blue-500'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Scan Tab */}
      {activeTab === 'scan' && (
        <div className="space-y-6">
          {/* Framework selection */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h2 className="text-2xl font-bold mb-4">Select Framework</h2>
            <div className="grid grid-cols-5 gap-4">
              {frameworks.map(framework => (
                <button
                  key={framework.slug}
                  onClick={() => setSelectedFramework(framework.slug)}
                  className={`p-6 rounded-lg border-2 transition-all ${
                    selectedFramework === framework.slug
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                  }`}
                >
                  <div className="text-center">
                    <Network className="mx-auto mb-2" size={32} />
                    <div className="font-semibold">{framework.name}</div>
                    {/* <div className="text-xs mt-1">
                      {framework.supports_scan && framework.supports_agentic_test ? (
                        <span className="text-green-400">Scans + Agentic Tests Supported</span>
                      ) : framework.supports_scan ? (
                        <span className="text-yellow-400">Scans Only (No Agentic Tests)</span>
                      ) : (
                        <span className="text-red-400">Not Supported</span>
                      )}
                    </div> */}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* File upload + config */}
          <div className="grid grid-cols-2 gap-6">
            {/* File Upload */}
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Upload size={24} /> Upload Workflow Files
              </h3>

              <div
                className="border-2 border-dashed border-gray-700 rounded-lg p-12 text-center transition-all hover:border-blue-500 hover:bg-gray-800/50 cursor-pointer"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const droppedFiles = Array.from(e.dataTransfer.files).filter(f =>
                    f.name.endsWith('.py')
                  );
                  if (droppedFiles.length === 0) {
                    showAlert('warning', 'Only .py files are allowed.');
                    return;
                  }
                  setFiles((prev) => [...prev, ...droppedFiles]);
                }}
              >
                <input
                  id="fileUploadInput"
                  type="file"
                  multiple
                  className="hidden"
                  accept=".py"
                  onChange={onFileChange}
                />
                <Upload className="mx-auto mb-4 text-gray-500" size={48} />
                <p className="text-gray-400 mb-2">Drag and drop your workflow files here</p>
                <p className="text-sm text-gray-500 mb-4">or click to browse</p>
                <label
                  htmlFor="fileUploadInput"
                  className="inline-flex items-center justify-center px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold text-white cursor-pointer transition-colors"
                >
                  Select Files
                </label>

                {files.length > 0 && (
                  <div className="mt-6 text-left space-y-2">
                    <div className="text-gray-300 font-semibold mb-2">Selected Files:</div>
                    <ul className="max-h-48 overflow-y-auto text-sm text-gray-400 bg-gray-900/50 rounded-lg p-3 border border-gray-800">
                      {files.map((f, i) => (
                        <li
                          key={i}
                          className="flex justify-between items-center border-b border-gray-800 last:border-none py-2 px-1 hover:bg-gray-800/50 rounded transition"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-200">{f.name}</span>
                            <span className="text-xs text-gray-500">
                              {(f.size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              setFiles((prev) => prev.filter((_, idx) => idx !== i))
                            }
                            className="text-white hover:text-red-400 w-6 h-6 bg-red-500/50 rounded-lg transition-colors text-sm"
                            title="Remove file"
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Scan Config */}
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileCode size={24} /> Scan Configuration
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Scan Type</label>
                  <select
                    value={scanType}
                    onChange={(e) => setScanType(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
                  >
                    <option>Static Analysis</option>
                    {/* <option>Dynamic Testing</option>
                    <option>Full Scan</option> */}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Output Format</label>
                  <select className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg">
                    <option>HTML Report</option>
                    <option>JSON</option>
                    <option>PDF</option>
                  </select>
                </div>

                <button
                  disabled={running || files.length === 0}
                  onClick={() => startScan(false)}
                  className="w-full mt-12 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Play size={20} /> {running ? 'Scanning...' : 'Start Scan'}
                </button>
              </div>
            </div>
          </div>

          {/* Scan Details Display (if available) */}
          {scanDetails.projectName && (
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4">Last Scan Details</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">Tenant Name</div>
                  <div className="font-mono text-gray-200">{tenantName}</div>
                </div>
                <div>
                  <div className="text-gray-400">Application Name</div>
                  <div className="font-mono text-gray-200">{selectedApp}</div>
                </div>
                <div>
                  <div className="text-gray-400">Timestamp</div>
                  <div className="text-gray-200">{scanDetails.timestamp}</div>
                </div>
                <div>
                  <div className="text-gray-400">Framework</div>
                  <div className="text-gray-200 capitalize">{scanDetails.framework}</div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <Network className="text-purple-500" size={24} />
                <span className="text-gray-400 text-sm">Agents Detected</span>
              </div>
              <div className="text-3xl font-bold">{findings.agents || agents.length}</div>
            </div>
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <Wrench className="text-cyan-500" size={24} />
                <span className="text-gray-400 text-sm">Tools Identified</span>
              </div>
              <div className="text-3xl font-bold">{findings.tools || tools.length}</div>
            </div>
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <Server className="text-green-500" size={24} />
                <span className="text-gray-400 text-sm">MCP Servers</span>
              </div>
              <div className="text-3xl font-bold">{mcpServers.length}</div>
            </div>
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <AlertTriangle className="text-orange-500" size={24} />
                <span className="text-gray-400 text-sm">Vulnerabilities</span>
              </div>
              <div className="text-3xl font-bold">{findings.vulnerabilities || activeVulnerabilities.length}</div>
            </div>
          </div>
        </div>
      )}

      {/* Workflow Tab */}
      {activeTab === 'workflow' && (
        <div className="space-y-6">
          {/* Workflow Visualization */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Workflow Visualization</h2>
              {/* <button className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 transition-colors">
                <Download size={18} />
                Export Graph
              </button> */}
            </div>
            {/* Legend - exact icons from the report */}
            <div className="flex items-center gap-6 mb-4 px-2 flex-wrap">
              <span className="text-sm text-gray-400 font-semibold">Legend:</span>

              {/* Agent */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="12" fill="#164EAF"/>
                  <path d="M12 7V8M10.5 12V16.5M13.5 12V16.5M8.5 14L10.5 13M13.5 13L15.5 14M10.5 15H13.5M11 10V10.005M13 10V10.005M9 9C9 8.73478 9.10536 8.48043 9.29289 8.29289C9.48043 8.10536 9.73478 8 10 8H14C14.2652 8 14.5196 8.10536 14.7071 8.29289C14.8946 8.48043 15 8.73478 15 9V11C15 11.2652 14.8946 11.5196 14.7071 11.7071C14.5196 11.8946 14.2652 12 14 12H10C9.73478 12 9.48043 11.8946 9.29289 11.7071C9.10536 11.5196 9 11.2652 9 11V9Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm text-gray-300">Agent</span>
              </div>

              {/* Tool */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#A738D6"/>
                  <path d="M9.9281 10.9999H11.4281V9.49993L9.6781 7.74993C10.2379 7.48256 10.8669 7.39533 11.4783 7.50024C12.0898 7.60515 12.6536 7.89704 13.0923 8.33571C13.531 8.77439 13.8229 9.33828 13.9278 9.94973C14.0327 10.5612 13.9455 11.1901 13.6781 11.7499L16.6781 14.7499C16.877 14.9488 16.9888 15.2186 16.9888 15.4999C16.9888 15.7812 16.877 16.051 16.6781 16.2499C16.4792 16.4488 16.2094 16.5606 15.9281 16.5606C15.6468 16.5606 15.377 16.4488 15.1781 16.2499L12.1781 13.2499C11.6183 13.5173 10.9894 13.6045 10.3779 13.4996C9.76646 13.3947 9.20257 13.1028 8.76389 12.6641C8.32521 12.2255 8.03332 11.6616 7.92842 11.0501C7.82351 10.4387 7.91074 9.80974 8.1781 9.24993L9.9281 10.9999Z" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm text-gray-300">Tool</span>
              </div>

              {/* CustomTool */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#F5DDFF"/>
                  <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A738D6" strokeDasharray="2 2"/>
                  <path d="M9.3562 10.9999H10.8562V9.49993L9.1062 7.74993C9.66602 7.48256 10.295 7.39533 10.9064 7.50024C11.5178 7.60515 12.0817 7.89704 12.5204 8.33571C12.9591 8.77439 13.251 9.33828 13.3559 9.94973C13.4608 10.5612 13.3736 11.1901 13.1062 11.7499L16.1062 14.7499C16.3051 14.9488 16.4169 15.2186 16.4169 15.4999C16.4169 15.7812 16.3051 16.051 16.1062 16.2499C15.9073 16.4488 15.6375 16.5606 15.3562 16.5606C15.0749 16.5606 14.8051 16.4488 14.6062 16.2499L11.6062 13.2499C11.0464 13.5173 10.4175 13.6045 9.806 13.4996C9.19455 13.3947 8.63066 13.1028 8.19199 12.6641C7.75331 12.2255 7.46142 11.6616 7.35651 11.0501C7.2516 10.4387 7.33884 9.80974 7.6062 9.24993L9.3562 10.9999Z" stroke="black" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm text-gray-300">CustomTool</span>
              </div>

              {/* Basic */}
              <div className="flex items-center gap-2">
                <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_basic_react)">
                    <rect x="14.1421" width="20" height="20" rx="4" transform="rotate(45 14.1421 0)" fill="#009C92"/>
                    <path d="M16.2635 12.0207L17.3241 10.9601M17.1473 14.142L18.6676 14.142M16.2635 16.2634L17.3241 17.324M14.1421 17.1472V18.6675M12.0208 16.2634L10.9602 17.324M11.1369 14.142L9.61665 14.142M12.0208 12.0207L10.9602 10.9601M14.1421 11.1368V9.61655" stroke="white" strokeLinecap="round" strokeLinejoin="round"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_basic_react">
                      <rect x="14.1421" width="20" height="20" rx="4" transform="rotate(45 14.1421 0)" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-sm text-gray-300">Basic</span>
              </div>

              {/* MCP Server */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="24" height="24" rx="4" fill="#FFDD00"/>
                  <path d="M14.1363 3.01758C14.1531 3.02925 14.1598 3.04904 14.1704 3.05141C14.5728 3.14116 14.9416 3.30574 15.2787 3.54292C15.655 3.80761 15.9668 4.13138 16.2018 4.5288C16.3938 4.85362 16.5297 5.20145 16.5826 5.57269C16.6201 5.83557 16.6127 6.10502 16.6217 6.37166C16.6234 6.42174 16.6079 6.4724 16.6014 6.51556C16.7039 6.51556 16.7977 6.51367 16.8913 6.51597C17.0658 6.52025 17.2419 6.51369 17.4141 6.53613C17.6158 6.56241 17.8156 6.60838 18.0134 6.65752C18.2643 6.71985 18.4932 6.83719 18.7164 6.96531C19.0309 7.1458 19.2905 7.39318 19.5294 7.65964C19.7673 7.92515 19.9495 8.23099 20.0663 8.56925C20.137 8.7741 20.2058 8.98351 20.2385 9.19654C20.2746 9.43153 20.2936 9.66993 20.2666 9.91267C20.2368 10.1801 20.1926 10.4413 20.1041 10.6949C19.9835 11.0402 19.8242 11.3682 19.5745 11.6363C19.22 12.0169 18.8478 12.3811 18.4817 12.7508C18.4021 12.8312 18.3143 12.9033 18.2337 12.9828C17.7172 13.4921 17.202 14.0028 16.6856 14.5122C16.386 14.8076 16.0849 15.1015 15.7845 15.3961C15.4821 15.6927 15.1795 15.9893 14.8771 16.286C14.5746 16.5826 14.2722 16.8793 13.9697 17.1759C13.6693 17.4705 13.3693 17.7655 13.0681 18.0593C12.9913 18.1342 12.9088 18.2033 12.8325 18.2786C12.7268 18.3828 12.728 18.5037 12.8349 18.6092C13.1928 18.9623 13.5519 19.3142 13.9101 19.667C14.0149 19.7702 14.1248 19.8692 14.2208 19.9802C14.387 20.1722 14.3746 20.5389 14.2103 20.7334C14.0645 20.906 13.8811 20.9904 13.6573 20.9814C13.4962 20.9749 13.3518 20.9153 13.2365 20.8024C12.7893 20.3646 12.3459 19.923 11.8989 19.4851C11.7685 19.3573 11.6586 19.2155 11.5874 19.048C11.4822 18.8006 11.433 18.5464 11.4686 18.2726C11.5045 17.9967 11.6034 17.7543 11.7717 17.5368C11.7875 17.5163 11.8041 17.4962 11.8224 17.4779C12.0582 17.2414 12.2933 17.0042 12.5309 16.7695C12.8426 16.4614 13.1563 16.1552 13.4693 15.8484C13.7426 15.5805 14.0164 15.3132 14.2895 15.0453C14.6259 14.7153 14.9615 14.3845 15.2982 14.0548C15.515 13.8424 15.7338 13.632 15.9504 13.4193C16.3343 13.0425 16.7169 12.6644 17.1005 12.2873C17.4047 11.9882 17.709 11.6893 18.0143 11.3913C18.154 11.255 18.3 11.125 18.436 10.9852C18.7254 10.6878 18.9105 10.3382 18.9618 9.92091C19.0269 9.39158 18.8929 8.92352 18.5566 8.50886C18.2712 8.15694 17.9077 7.92924 17.4725 7.82136C17.2447 7.76492 17.0102 7.76874 16.7752 7.79666C16.4212 7.83871 16.1146 7.98603 15.8333 8.19457C15.7662 8.24429 15.7061 8.30409 15.6466 8.36313C15.397 8.61078 15.1501 8.86103 14.9001 9.10819C14.5822 9.42252 14.2622 9.73483 13.9431 10.0479C13.6469 10.3385 13.3506 10.6289 13.0544 10.9194C12.7415 11.2263 12.4286 11.5333 12.1157 11.8402C11.8153 12.1348 11.5149 12.4293 11.2145 12.7239C10.9058 13.0267 10.5964 13.3289 10.2886 13.6326C10.043 13.8749 9.73211 13.9159 9.45742 13.7689C9.09264 13.5737 9.02687 13.0867 9.30381 12.7906C9.45381 12.6303 9.61264 12.4781 9.76915 12.3239C10.073 12.0245 10.3781 11.7262 10.6826 11.4275C10.9872 11.1288 11.2917 10.8301 11.5963 10.5314C11.903 10.2307 12.2097 9.92999 12.5164 9.62927C12.8168 9.3347 13.1172 9.04016 13.4176 8.74556C13.7263 8.44275 14.0345 8.1394 14.3439 7.83724C14.494 7.69063 14.6507 7.55048 14.7965 7.39982C15.0678 7.11952 15.2395 6.78926 15.3073 6.40021C15.3986 5.87648 15.268 5.40585 14.9599 4.99221C14.6797 4.616 14.2998 4.36613 13.8276 4.28434C13.6669 4.25652 13.5018 4.22579 13.3408 4.23482C12.939 4.25736 12.5698 4.38636 12.251 4.63985C12.1854 4.69197 12.1241 4.74987 12.0642 4.80854C11.7113 5.15417 11.3596 5.50102 11.0071 5.84706C10.7256 6.12333 10.4435 6.39889 10.1619 6.67502C9.84064 6.99012 9.51967 7.30556 9.19844 7.62073C8.89811 7.91539 8.5976 8.20986 8.29723 8.50446C7.98849 8.80727 7.67982 9.11015 7.37111 9.41298C7.06867 9.70966 6.76622 10.0063 6.46375 10.303C6.1571 10.6038 5.85071 10.9048 5.54367 11.2051C5.32248 11.4215 5.10237 11.639 4.87826 11.8523C4.5992 12.1179 4.19348 12.1136 3.9366 11.8514C3.68652 11.5961 3.69371 11.2199 3.93871 10.9685C4.3018 10.596 4.67811 10.2363 5.0495 9.87188C5.28289 9.64286 5.51812 9.41571 5.75152 9.18669C6.12083 8.82431 6.48905 8.46083 6.85817 8.09826C7.14161 7.81985 7.42584 7.54226 7.70948 7.26405C8.03494 6.94483 8.36005 6.62524 8.68547 6.30597C8.97954 6.01748 9.27387 5.72927 9.568 5.44085C9.87676 5.13806 10.1852 4.8349 10.4942 4.53241C10.7279 4.30369 10.9577 4.07076 11.1979 3.84903C11.4031 3.6596 11.6262 3.49244 11.8749 3.36038C12.1407 3.2193 12.4163 3.10865 12.7117 3.04881C12.7192 3.0473 12.7248 3.03635 12.726 3.02369C13.1898 3.01758 13.6589 3.01758 14.1363 3.01758Z" fill="black"/>
                </svg>
                <span className="text-sm text-gray-300">MCP Server</span>
              </div>
            </div>
            {!graphData ? (
              <div className="bg-gray-950 rounded-lg p-16 min-h-96 flex flex-col items-center justify-center">
                <Network className="text-gray-700 mb-4" size={64} />
                <div className="text-gray-500 text-lg mb-2">No workflow data available</div>
                <div className="text-gray-600 text-sm">Run a scan to visualize the workflow structure</div>
              </div>
            ) : (
              <div ref={graphContainerRef}
                className="bg-transparent rounded-lg p-4"
                style={{ height: '650px' }}

              >           
                <ForceGraph2D
                  graphData={processedGraphData}
                  //nodeId="name"
                  nodeLabel="label"
                  nodeRelSize={8}
                  linkDirectionalParticles={2}
                  linkDirectionalParticleSpeed={0.005}
                  linkDirectionalArrowLength={6}
                  linkDirectionalArrowRelPos={1}
                  backgroundColor="#000000"
                  width={graphContainerRef.current?.clientWidth || 1400 }
                  height={graphContainerRef.current?.clientHeight || 600 }


                  // Make nodes readable (text labels)
                 nodeCanvasObject={(node, ctx, globalScale) => {
                    const label = node.label || node.id || node.name;
                    const fontSize = 12;

                    if (node.image) {
                      if (!node.__img) {
                        const img = new Image();
                        img.src = node.image;
                        node.__img = img;
                      }
                      const size = 16;
                      if (node.__img.complete) {
                        ctx.drawImage(node.__img, node.x - size / 2, node.y - size / 2, size, size);
                      }
                    }

                    // Draw text below the circle
                    ctx.font = `${fontSize}px Sans-Serif`;
                    ctx.fillStyle = "white";
                    ctx.textAlign = "center";
                    ctx.textBaseline = "top"; // <-- this puts text BELOW
                    ctx.fillText(label, node.x, node.y + 10); // <-- push downward
                  }}

                  nodeCanvasObjectMode={() => "after"} // render label AFTER nodes


                  // Set brighter visible links
                  linkColor={() => "rgba(255,255,255,0.3)"}

                  // Keep nodes visible on top of labels
                  // nodeCanvasObjectMode={() => "before"}
                  
                  // Your original coloring logic
                  nodeColor={node => {
                    if (node.type === 'agent') return '#a855f7';
                    if (node.type === 'tool') return '#06b6d4';
                    if (node.type === 'mcp_server') return '#10b981';
                    return '#6b7280';
                  }}
                />

              </div>
            )}
          </div>

          {/* Agents and Tools */}
          <div className="grid grid-cols-2 gap-6">
            {/* Detected Agents */}
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Network className="text-purple-500" size={24} />
                Detected Agents ({agents.length})
              </h3>
              {agents.length === 0 ? (
                <div className="bg-gray-800/30 p-8 rounded-lg text-center">
                  <Network className="mx-auto mb-3 text-gray-600" size={40} />
                  <div className="text-gray-500">No agents detected</div>
                  <div className="text-gray-600 text-sm mt-1">Run a scan to identify agents</div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {agents.map((agent, idx) => (
                    <div key={idx} className="bg-gray-800/50 p-4 rounded-lg hover:bg-gray-800 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{agent.name}</div>
                        <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs capitalize">
                          {agent.type}
                        </span>
                      </div>
                      <div className="text-sm text-gray-400">
                        System Prompt: <span className="text-gray-300">{agent.systemPrompt}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Integrated Tools */}
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Wrench className="text-cyan-500" size={24} />
                Integrated Tools ({tools.length})
              </h3>
              {tools.length === 0 ? (
                <div className="bg-gray-800/30 p-8 rounded-lg text-center">
                  <Wrench className="mx-auto mb-3 text-gray-600" size={40} />
                  <div className="text-gray-500">No tools identified</div>
                  <div className="text-gray-600 text-sm mt-1">Run a scan to discover tools</div>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {tools.map((tool, idx) => (
                    <div key={idx} className="bg-gray-800/50 p-4 rounded-lg hover:bg-gray-800 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{tool.name}</div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 ${getSeverityColor(tool.risk)}/20 ${getSeverityTextColor(tool.risk)} rounded-full text-xs capitalize`}>
                            {tool.risk}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-400 mb-1">
                        Category: <span className="text-gray-300">{tool.category}</span>
                      </div>
                      {tool.vulnerabilityCount > 0 && (
                        <div className="text-sm text-orange-400">
                          ⚠️ {tool.vulnerabilityCount} vulnerability{tool.vulnerabilityCount > 1 ? 'ies' : 'y'} found
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* MCP Servers */}
          {mcpServers.length > 0 && (
            <div className="bg-transparent p-6 rounded-xl border border-gray-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Server className="text-green-500" size={24} />
                MCP Servers ({mcpServers.length})
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {mcpServers.map((server, idx) => (
                  <div key={idx} className="bg-gray-800/50 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold">{server.name}</div>
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs capitalize">
                        {server.status}
                      </span>
                    </div>
                    <div className="text-sm text-gray-400">
                      Connections: {server.connections}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vulnerabilities Tab */}
      {activeTab === 'vulnerabilities' && (
        <div className="space-y-6">
          {/* Vulnerability Summary */}
          <div className="grid grid-cols-4 gap-6">
            {['critical', 'high', 'medium', 'low'].map((severity) => {
              const count = vulnerabilities.filter(v => v.severity === severity && !v.mitigated).length;
              return (
                <div key={severity} className="bg-transparent p-6 rounded-xl border border-gray-800">
                  <div className={`text-4xl font-bold ${getSeverityTextColor(severity)} mb-2`}>{count}</div>
                  <div className="text-gray-400 capitalize">{severity} Severity</div>
                </div>
              );
            })}
          </div>

          {/* Vulnerability List */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h2 className="text-2xl font-bold mb-6">Detected Vulnerabilities</h2>
            {vulnerabilities.length === 0 ? (
              <div className="bg-gray-800/30 p-12 rounded-lg text-center">
                <AlertTriangle className="mx-auto mb-4 text-gray-600" size={56} />
                <div className="text-gray-400 text-lg mb-2">No vulnerabilities detected</div>
                <div className="text-gray-600 text-sm">Run a scan to identify potential security issues</div>
              </div>
            ) : (
              <div className="space-y-4">
                {vulnerabilities.map((vuln) => (
                  <div key={vuln.id} className={`bg-gray-800/50 p-6 rounded-lg border-l-4 ${
                    vuln.severity === 'critical' ? 'border-red-500' :
                    vuln.severity === 'high' ? 'border-orange-500' :
                    vuln.severity === 'medium' ? 'border-yellow-500' : 'border-green-500'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold">{vuln.name}</h3>
                          <span className={`px-3 py-1 ${getSeverityColor(vuln.severity)}/20 ${getSeverityTextColor(vuln.severity)} rounded-full text-xs capitalize font-semibold`}>
                            {vuln.severity}
                          </span>
                          {vuln.mitigated && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs flex items-center gap-1">
                              <CheckCircle size={14} />
                              Mitigated
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mb-3">{vuln.description}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-500">Tool: <span className="text-blue-400">{vuln.toolName}</span></span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-500">Framework: <span className="text-purple-400">{vuln.framework}</span></span>
                        </div>
                      </div>
                    </div>
                    {vuln.affected && vuln.affected.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <div className="text-sm text-gray-400 mb-2">Affected Components:</div>
                        <div className="flex flex-wrap gap-2">
                          {vuln.affected.map((component, idx) => (
                            <span key={idx} className="px-3 py-1 bg-gray-700/50 rounded-lg text-xs">
                              {component}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {vuln.remediationSteps && vuln.remediationSteps.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-700">
                        <div className="text-sm text-gray-400 mb-2">Remediation Steps:</div>
                        <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
                          {vuln.remediationSteps.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Framework Mapping */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h3 className="text-xl font-bold mb-4">Security Framework Mapping</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800/50 p-4 rounded-lg">
                <div className="font-semibold mb-2">OWASP LLM Top 10</div>
                <div className="text-2xl font-bold text-blue-400">
                  {vulnerabilities.filter(v => v.framework === 'OWASP LLM Top 10:').length}
                </div>
                <div className="text-sm text-gray-400 mt-1">Mapped Vulnerabilities</div>
              </div>
              <div className="bg-gray-800/50 p-4 rounded-lg">
                <div className="font-semibold mb-2">OWASP Agentic AI</div>
                <div className="text-2xl font-bold text-purple-400">
                  {
                    vulnerabilities.filter(v =>
                      normalizeFW(v.framework) === "OWASP Agentic"
                    ).length
                  }
                </div>
                <div className="text-sm text-gray-400 mt-1">Mapped Vulnerabilities</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          {/* Test Configuration */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h2 className="text-2xl font-bold mb-4">Dynamic Security Testing</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Test Suite</label>
                <select className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg">
                  <option>Default Security Tests</option>
                  <option>Custom Test Configuration</option>
                  <option>OWASP LLM Tests</option>
                  <option>Prompt Injection Suite</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">API Key Configuration</label>
                <select className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg">
                  <option>OpenAI API Key</option>
                  <option>Azure OpenAI</option>
                  <option>Custom LLM</option>
                </select>
              </div>
            </div>
            <button 
              disabled={!supportsTests || running}
              onClick={() => runTests()}
              className="mt-4 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play size={20} />
              Run Tests
            </button>
            {!supportsTests && (
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="text-yellow-400 text-sm">
                  ⚠️ The selected framework does not support agentic testing.
                </div>
              </div>
            )}
          </div>

          {/* Test Results - Empty State */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h3 className="text-xl font-bold mb-4">Test Results</h3>
            <div className="bg-gray-800/30 p-12 rounded-lg text-center">
              <Play className="mx-auto mb-4 text-gray-600" size={56} />
              <div className="text-gray-400 text-lg mb-2">No test results yet</div>
              <div className="text-gray-600 text-sm">Run tests to see security validation results</div>
            </div>
          </div>

          {/* Custom Test Configuration */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h3 className="text-xl font-bold mb-4">Custom Test Payloads</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Test Prompt</label>
                <textarea 
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm font-mono h-32"
                  placeholder="Enter custom test prompt or injection payload..."
                />
              </div>
              <div className="flex gap-4">
                <button className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors">
                  Load Template
                </button>
                <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                  Execute Test
                </button>
              </div>
            </div>
          </div>

          {/* Attack Patterns Library */}
          <div className="bg-transparent p-6 rounded-xl border border-gray-800">
            <h3 className="text-xl font-bold mb-4">Common Attack Patterns</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                'Prompt Injection',
                'Jailbreak Attempts',
                'Data Exfiltration',
                'Tool Misuse',
                'Context Poisoning',
                'Indirect Prompt Injection'
              ].map((pattern, idx) => (
                <button
                  key={idx}
                  className="p-4 bg-gray-800/50 hover:bg-gray-800 rounded-lg text-left transition-colors"
                >
                  <div className="font-semibold mb-1">{pattern}</div>
                  <div className="text-xs text-gray-400">Click to load test</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      </>)}
    </div>
  );
};

export default AgentOps;

